import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Card, Button, Input, Select, Form, Tag,
  Modal, message, Space, Divider, Typography, Badge
} from 'antd';
import {
  CustomerServiceOutlined, PlusOutlined, MessageOutlined,
  ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  UserOutlined, ReloadOutlined
} from '@ant-design/icons';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { getAudience } from '../../config/permissions';
import { SYSTEM_PAGE_CSS } from '../landlord/systemPageStyles';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface SupportTicket {
  id: string;
  subject: string;
  message: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  createdAt: string;
  updatedAt: string;
  submitter_name?: string;
  submitter_email?: string;
  responses?: SupportResponse[];
}

interface SupportResponse {
  id: string;
  message: string;
  isFromUser: boolean;
  createdAt: string;
  responder_name?: string;
  responder_email?: string;
}

const PRIORITY_COLOR: Record<string, string> = {
  urgent: '#dc2626', high: '#ea580c', medium: '#2563eb', low: '#16a34a'
};
const STATUS_COLOR: Record<string, string> = {
  open: '#2563eb', 'in-progress': '#d97706', resolved: '#16a34a', closed: '#9ca3af'
};
const STATUS_BG: Record<string, string> = {
  open: '#eff6ff', 'in-progress': '#fffbeb', resolved: '#f0fdf4', closed: '#f9fafb'
};

const Support: React.FC = () => {
  const { token, role } = useAuth();
  const isLandlord = getAudience(localStorage.getItem('business_type')) === 'landlord';
  const [tickets, setTickets]             = useState<SupportTicket[]>([]);
  const [loading, setLoading]             = useState(true);
  const [showCreate, setShowCreate]       = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [replyText, setReplyText]         = useState('');
  const [sendingReply, setSendingReply]   = useState(false);
  const [statusFilter, setStatusFilter]   = useState<string>('all');
  const [stats, setStats]                 = useState({ total: 0, open: 0, inProgress: 0, urgent: 0 });
  const [form] = Form.useForm();

  const isStaffOrAdmin = role === 'Admin' || role === 'Manager' || role === 'Staff';

  useEffect(() => { if (token) { loadTickets(); loadStats(); } }, [token]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/support/tickets`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setTickets(d.tickets || []); }
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const r = await apiFetch(`${API_URL}/support/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setStats(d.stats); }
    } catch { /* silent */ }
  };

  const openTicketDetail = async (ticket: SupportTicket) => {
    try {
      const r = await apiFetch(`${API_URL}/support/tickets/${ticket.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setSelectedTicket(d.ticket); }
      else setSelectedTicket(ticket);
    } catch { setSelectedTicket(ticket); }
  };

  const createTicket = async (values: any) => {
    try {
      const r = await apiFetch(`${API_URL}/support/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(values)
      });
      if (r.ok) {
        message.success('Support ticket created!');
        setShowCreate(false);
        form.resetFields();
        loadTickets(); loadStats();
      } else { message.error('Failed to create ticket'); }
    } catch { message.error('Failed to create ticket'); }
  };

  const sendReply = async () => {
    if (!selectedTicket || !replyText.trim()) return;
    setSendingReply(true);
    try {
      const r = await apiFetch(`${API_URL}/support/tickets/${selectedTicket.id}/responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: replyText })
      });
      if (r.ok) {
        message.success('Reply sent!');
        setReplyText('');
        const d2 = await apiFetch(`${API_URL}/support/tickets/${selectedTicket.id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (d2.ok) { const j = await d2.json(); setSelectedTicket(j.ticket); }
        loadTickets(); loadStats();
      } else { message.error('Failed to send reply'); }
    } catch { message.error('Failed to send reply'); }
    finally { setSendingReply(false); }
  };

  const updateStatus = async (newStatus: string) => {
    if (!selectedTicket) return;
    try {
      const r = await apiFetch(`${API_URL}/support/tickets/${selectedTicket.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      if (r.ok) {
        message.success(`Status → ${newStatus}`);
        const d2 = await apiFetch(`${API_URL}/support/tickets/${selectedTicket.id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (d2.ok) { const j = await d2.json(); setSelectedTicket(j.ticket); }
        loadTickets(); loadStats();
      } else { message.error('Failed to update status'); }
    } catch { message.error('Failed to update status'); }
  };

  const filtered = statusFilter === 'all' ? tickets : tickets.filter(t => t.status === statusFilter);

  return (
    <div className={isLandlord ? 'sys-page' : 'page-layout'}>
      {isLandlord && <style>{SYSTEM_PAGE_CSS}</style>}
      {isLandlord ? (
        <div className="sys-banner-wrap sys-banner-sales">
          <div className="sys-banner-glass">
            <div>
              <h1>🎧 Support</h1>
              <p>{isStaffOrAdmin ? 'View and respond to team support tickets' : 'Raise and track tickets with the ShahoSuite team'}</p>
            </div>
            <div className="sys-banner-actions">
              <button type="button" className="sys-btn-glass" onClick={() => { loadTickets(); loadStats(); }}>↻ Refresh</button>
              <button type="button" className="sys-btn-white" onClick={() => setShowCreate(true)}>+ New ticket</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>🆘 Support</h1>
            <p>{isStaffOrAdmin ? 'View and respond to all support tickets from your team' : 'Raise and track your support tickets'}</p>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => { loadTickets(); loadStats(); }}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreate(true)}>New Ticket</Button>
          </Space>
        </div>
      )}

      {/* Stats bar */}
      <div className={isLandlord ? 'sys-kpi-row' : undefined} style={isLandlord ? undefined : { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', value: stats.total, color: '#374151', bg: '#f9fafb', border: '#e5e7eb' },
          { label: 'Open', value: stats.open, color: '#2563eb', bg: '#eff6ff', border: '#93c5fd' },
          { label: 'In Progress', value: stats.inProgress, color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
          { label: 'Urgent', value: stats.urgent, color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
        ].map((s, i) => (
          isLandlord ? (
            <div key={s.label} className={`sys-kpi ${i % 2 === 0 ? 'sys-kpi-white' : 'sys-kpi-blue'}`}>
              <div className="kl">{s.label}</div>
              <div className="kv">{s.value}</div>
            </div>
          ) : (
            <div key={s.label} style={{
              flex: '1 1 120px', padding: '14px 18px', borderRadius: 10,
              background: s.bg, border: `1px solid ${s.border}`, textAlign: 'center'
            }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{s.label}</div>
            </div>
          )
        ))}
      </div>

      {/* Contact ShahoSuite card */}
      <div style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        borderRadius: 12,
        padding: '20px 24px',
        marginBottom: 20,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12
      }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
            📧 Need help from the ShahoSuite team?
          </div>
          <div style={{ color: '#bfdbfe', fontSize: 13 }}>
            Email us directly and we'll respond within 24 hours.
          </div>
        </div>
        <a
          href="mailto:admin@shahosuite.com"
          style={{
            display: 'inline-block',
            background: '#fff',
            color: '#1e40af',
            fontWeight: 700,
            fontSize: 14,
            padding: '10px 22px',
            borderRadius: 8,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          📧 Email ShahoSuite Support
        </a>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        {['all', 'open', 'in-progress', 'resolved', 'closed'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{
            padding: '7px 18px', border: 'none', borderRadius: 6, cursor: 'pointer',
            background: statusFilter === s ? '#2563eb' : '#f3f4f6',
            color: statusFilter === s ? '#fff' : '#374151',
            fontWeight: statusFilter === s ? 700 : 400, fontSize: 13
          }}>
            {s === 'all' ? 'All' : s === 'in-progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}
            {s === 'open' && stats.open > 0 && (
              <span style={{ marginLeft: 6, background: '#dc2626', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{stats.open}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tickets list */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>Loading tickets…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
            <CustomerServiceOutlined style={{ fontSize: 40, marginBottom: 12, display: 'block' }} />
            {statusFilter === 'all' ? 'No tickets yet. Click "New Ticket" to raise one.' : `No ${statusFilter} tickets.`}
          </div>
        ) : (
          filtered.map((ticket, idx) => (
            <div
              key={ticket.id}
              onClick={() => openTicketDetail(ticket)}
              style={{
                padding: '16px 20px', cursor: 'pointer',
                borderBottom: idx < filtered.length - 1 ? '1px solid #f3f4f6' : 'none',
                display: 'flex', alignItems: 'center', gap: 16,
                transition: 'background 0.15s'
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
              onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
            >
              {/* Status dot */}
              <div style={{
                width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                background: STATUS_COLOR[ticket.status] || '#9ca3af'
              }} />

              {/* Main content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ticket.subject}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {isStaffOrAdmin && ticket.submitter_name && (
                    <span style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <UserOutlined style={{ fontSize: 10 }} /> {ticket.submitter_name}
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>
                    {ticket.category} · {new Date(ticket.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              </div>

              {/* Tags */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <span style={{
                  padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                  background: STATUS_BG[ticket.status], color: STATUS_COLOR[ticket.status],
                  border: `1px solid ${STATUS_COLOR[ticket.status]}40`
                }}>
                  {ticket.status === 'in-progress' ? 'IN PROGRESS' : ticket.status.toUpperCase()}
                </span>
                <span style={{
                  padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                  background: `${PRIORITY_COLOR[ticket.priority]}15`, color: PRIORITY_COLOR[ticket.priority],
                  border: `1px solid ${PRIORITY_COLOR[ticket.priority]}40`
                }}>
                  {ticket.priority.toUpperCase()}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Ticket Modal */}
      <Modal
        title="🆘 New Support Ticket"
        open={showCreate}
        onCancel={() => { setShowCreate(false); form.resetFields(); }}
        footer={null}
        width={580}
      >
        <Form form={form} layout="vertical" onFinish={createTicket} style={{ marginTop: 16 }}>
          <Form.Item name="subject" label="Subject" rules={[{ required: true, message: 'Please enter a subject' }]}>
            <Input placeholder="Brief description of your issue" />
          </Form.Item>
          <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Please select a category' }]}>
            <Select placeholder="Select issue category">
              <Option value="technical">Technical Issue</Option>
              <Option value="billing">Billing & Payments</Option>
              <Option value="account">Account & Login</Option>
              <Option value="booking">Booking System</Option>
              <Option value="feature">Feature Request</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>
          <Form.Item name="priority" label="Priority" rules={[{ required: true, message: 'Please select priority' }]}>
            <Select placeholder="How urgent is this?">
              <Option value="low">🟢 Low — General inquiry</Option>
              <Option value="medium">🔵 Medium — Minor issue</Option>
              <Option value="high">🟠 High — Major issue affecting work</Option>
              <Option value="urgent">🔴 Urgent — System down or critical</Option>
            </Select>
          </Form.Item>
          <Form.Item name="message" label="Message" rules={[{ required: true, message: 'Please describe your issue' }]}>
            <TextArea rows={6} placeholder="Please provide detailed information about your issue…" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setShowCreate(false); form.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit">Create Ticket</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Ticket Detail Modal */}
      <Modal
        title={
          <div style={{ paddingRight: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{selectedTicket?.subject}</div>
            {selectedTicket && (
              <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400, marginTop: 2 }}>
                #{selectedTicket.id?.slice(0, 8)} · {selectedTicket.category}
                {isStaffOrAdmin && selectedTicket.submitter_name && ` · From: ${selectedTicket.submitter_name}`}
              </div>
            )}
          </div>
        }
        open={!!selectedTicket}
        onCancel={() => { setSelectedTicket(null); setReplyText(''); }}
        footer={null}
        width={680}
      >
        {selectedTicket && (
          <div>
            {/* Status / Priority row + Admin status changer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Space>
                <span style={{ padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700, background: STATUS_BG[selectedTicket.status], color: STATUS_COLOR[selectedTicket.status], border: `1px solid ${STATUS_COLOR[selectedTicket.status]}50` }}>
                  {selectedTicket.status === 'in-progress' ? 'IN PROGRESS' : selectedTicket.status.toUpperCase()}
                </span>
                <span style={{ padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700, background: `${PRIORITY_COLOR[selectedTicket.priority]}15`, color: PRIORITY_COLOR[selectedTicket.priority], border: `1px solid ${PRIORITY_COLOR[selectedTicket.priority]}40` }}>
                  {selectedTicket.priority.toUpperCase()}
                </span>
              </Space>
              {isStaffOrAdmin && (
                <Select value={selectedTicket.status} onChange={updateStatus} size="small" style={{ width: 160 }}>
                  <Option value="open">Open</Option>
                  <Option value="in-progress">In Progress</Option>
                  <Option value="resolved">Resolved</Option>
                  <Option value="closed">Closed</Option>
                </Select>
              )}
            </div>

            {/* Original message */}
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 8 }}>
                {isStaffOrAdmin && selectedTicket.submitter_name
                  ? `🙍 ${selectedTicket.submitter_name} wrote:`
                  : '📝 Your message:'}
              </div>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.message}</Text>
            </div>

            {/* Responses thread */}
            {(selectedTicket.responses || []).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Conversation</div>
                {selectedTicket.responses!.map((resp, i) => (
                  <div key={i} style={{
                    marginBottom: 8, padding: '12px 14px', borderRadius: 8,
                    background: resp.isFromUser ? '#eff6ff' : '#f0fdf4',
                    borderLeft: `3px solid ${resp.isFromUser ? '#2563eb' : '#16a34a'}`
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: resp.isFromUser ? '#2563eb' : '#16a34a', marginBottom: 4 }}>
                      {resp.isFromUser
                        ? (isStaffOrAdmin && selectedTicket.submitter_name ? selectedTicket.submitter_name : 'You')
                        : (resp.responder_name || '🛠 Support Team')}
                    </div>
                    <Text style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{resp.message}</Text>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                      {new Date(resp.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Divider style={{ margin: '12px 0' }} />

            {/* Reply box */}
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
              {isStaffOrAdmin ? '🛠 Reply as Support Team' : '💬 Add Reply'}
            </div>
            <TextArea
              rows={4}
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder={isStaffOrAdmin ? 'Type your support response…' : 'Type your reply or additional details…'}
              style={{ marginBottom: 10 }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Created {new Date(selectedTicket.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · Updated {new Date(selectedTicket.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </Text>
              <Button type="primary" icon={<MessageOutlined />} onClick={sendReply} loading={sendingReply} disabled={!replyText.trim()}>
                Send Reply
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Support;